import { ArrowLeftOutlined } from '@ant-design/icons'
import { Button, Card, Descriptions, Space, Spin, Tag, Timeline, Typography } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import ErrorResult from '@/components/feedback/ErrorResult'
import { useWorkflowTrace } from '@/modules/workflowTraces/queries'
import { formatDateTime } from '@/utils/datetime'
import type { WorkflowTraceStage } from '@/types/workflowTrace'

const { Text } = Typography

const stageStatusLabel: Record<string, string> = {
  not_started: '未开始',
  pending: '待处理',
  current: '当前卡点',
  completed: '已完成',
  unknown: '未知',
}

const stageStatusColor: Record<string, string> = {
  not_started: 'default',
  pending: 'gold',
  current: 'processing',
  completed: 'success',
  unknown: 'default',
}

function renderStageTag(stage: WorkflowTraceStage) {
  return <Tag color={stageStatusColor[stage.status] || 'default'}>{stageStatusLabel[stage.status] || stage.status}</Tag>
}

export default function WorkflowTracePage() {
  const navigate = useNavigate()
  const { entryType, entryId } = useParams<{ entryType: string; entryId: string }>()
  const { data, isLoading, isError, error, refetch } = useWorkflowTrace(entryType || null, entryId || null)

  if (!entryType || !entryId) {
    return <ErrorResult message="流程入口无效" />
  }

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
  }

  if (isError || !data) {
    return <ErrorResult error={error} onRetry={() => refetch()} />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>返回</Button>
        <Text strong style={{ fontSize: 16 }}>流程链路详情</Text>
        <Tag>{data.entry.entry_type}</Tag>
        <Text type="secondary">#{data.entry.entry_id}</Text>
      </Space>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16 }}>
        <Card size="small" title="头部摘要">
          <Descriptions size="small" column={1}>
            <Descriptions.Item label="当前阶段">{data.summary.current_stage}</Descriptions.Item>
            <Descriptions.Item label="阻塞阶段">{data.summary.bottleneck_stage}</Descriptions.Item>
            <Descriptions.Item label="当前待办人">{data.summary.pending_owner || '-'}</Descriptions.Item>
            <Descriptions.Item label="最高报警级别">{data.summary.highest_alarm_level || '-'}</Descriptions.Item>
            <Descriptions.Item label="活跃报警数">{data.summary.active_alert_count}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card size="small" title="当前卡点">
          <Space orientation="vertical" size={6} style={{ width: '100%' }}>
            <Text>{data.summary.bottleneck_reason_text}</Text>
            <Text type="secondary">报警规则：{data.summary.bottleneck_reason_code}</Text>
          </Space>
        </Card>
      </div>

      <Card size="small" title="报警点">
        {data.alerts.length === 0 ? (
          <Text type="secondary">当前无报警点</Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.alerts.map((alert) => (
              <div
                key={`${alert.rule_code}-${alert.first_detected_at}`}
                style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}
              >
                <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                  <Space wrap>
                    <Text strong>{alert.rule_name}</Text>
                    <Tag color={alert.event_status === 'reminded' ? 'orange' : 'red'}>{alert.event_status}</Tag>
                    <Tag>升级级别 {alert.current_escalation_level}</Tag>
                  </Space>
                  <Text type="secondary">责任人：{alert.target_account || '-'}</Text>
                  <Text type="secondary">首次命中：{formatDateTime(alert.first_detected_at)}</Text>
                </Space>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card size="small" title="阶段状态">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }}>
          {data.stages.map((stage) => (
            <div
              key={stage.stage_code}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 12,
                minHeight: 96,
              }}
            >
              <Space orientation="vertical" size={6} style={{ width: '100%' }}>
                <Text strong>{stage.stage_name}</Text>
                <Text type="secondary">{stage.stage_code}</Text>
                {renderStageTag(stage)}
              </Space>
            </div>
          ))}
        </div>
      </Card>

      <Card size="small" title="处理入口">
        <Space wrap>
          {data.entities.message?.visible && data.actions.message && (
            <Button onClick={() => navigate(`/messages?messageId=${encodeURIComponent(data.actions.message!.message_id)}`)}>
              查看消息
            </Button>
          )}
          {data.entities.draft?.visible && data.actions.draft && (
            <Button onClick={() => navigate(`/drafts/${data.actions.draft!.draft_id}`)}>
              查看草稿
            </Button>
          )}
          {data.entities.ticket?.visible && data.actions.ticket && (
            <Button onClick={() => navigate(`/tickets/${data.actions.ticket!.mapping_id}`)}>
              查看工单
            </Button>
          )}
        </Space>
      </Card>

      <Card size="small" title="时间线">
        <Timeline
          items={data.timeline.map((item) => ({
            content: (
              <Space orientation="vertical" size={2}>
                <Text strong>{item.title}</Text>
                <Text type="secondary">{item.event_type} · {item.stage_code}</Text>
                <Text type="secondary">{formatDateTime(item.occurred_at)}</Text>
              </Space>
            ),
          }))}
        />
      </Card>
    </div>
  )
}
